import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import Button from 'src/components/form/Button';
import { InfoCircleSvg, SparkleSvg } from 'src/assets/dynamicSvg';

const Card = styled(PageCard)`
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
`;

const Top = styled.div`
  background: ${({ theme }) => theme.colors.cultured};
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  padding: 16px;
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 0;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.prussianBlue};
  caret-color: ${({ theme }) => theme.colors.gp1};
  min-height: 92px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }
  &:disabled {
    background: transparent;
  }
  /* Filled slots are read-only — signal "not editable" like the rest of the app's disabled inputs. */
  &:read-only {
    cursor: not-allowed;
    caret-color: transparent;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const HelperPill = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  padding: 8px 16px;
`;

const Bottom = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px;
`;

const AnalyseButton = styled(Button).attrs({ primary: true })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-transform: none;
`;

export const MAX_INPUT = 2000; // matches the BE `input_max_length` cap on the description (userInput)
const MAX_HEIGHT = 272; // ~10 lines at 16px / line-height 1.7; content beyond that scrolls

interface RiskDescriptionCardProps {
  value: string;
  onChange: (value: string) => void;
  onAnalyse: () => void;
  analysing: boolean;
  /** Filled slot (a past attempt): textarea is read-only and the Analyse button is hidden. */
  readOnly: boolean;
  /** Whether the Analyse button is enabled (parent decides: non-empty, not analysing, not a duplicate). */
  canAnalyse: boolean;
}

export interface RiskDescriptionCardHandle {
  /** Move focus + caret into the description textarea and scroll it into view. */
  focus: () => void;
}

export const RiskDescriptionCard = forwardRef<RiskDescriptionCardHandle, RiskDescriptionCardProps>(
  ({ value, onChange, onAnalyse, analysing, readOnly, canAnalyse }, ref) => {
    const taRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const el = taRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length; // caret to the end so the user picks up where they left off
        el.setSelectionRange(end, end);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    }));

  // Grow with content up to ~10 lines, then scroll. Re-runs on every value change (incl. "use this suggestion").
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  return (
    <Card>
      <Top>
        <TextArea
          ref={taRef}
          rows={3}
          readOnly={readOnly}
          value={value}
          maxLength={MAX_INPUT}
          placeholder="  Explain the risk in your own words. The more details you share, the better we can analyse."
          onChange={(e) => onChange(e.target.value)}
          data-cy="ai-risk-description"
        />
        <Meta>
          <HelperPill>
            <InfoCircleSvg size={14} style={{ flexShrink: 0 }} />
            <Poppins px={14} color="davysGrey" css="line-height: 1.7;">
              Descriptions with a source, event, and consequence helps you get the best result
            </Poppins>
          </HelperPill>
        </Meta>
      </Top>
      {/* Always visible; disabled off the editable placeholder slot (canAnalyse is false there). */}
      <Bottom>
        <AnalyseButton
          type="button"
          disabled={!canAnalyse}
          loading={analysing}
          onClick={onAnalyse}
          data-cy="ai-analyse"
        >
          <SparkleSvg size={14} color="#FFFFFF" />
          ANALYSE SCENARIO
        </AnalyseButton>
      </Bottom>
    </Card>
    );
  },
);

RiskDescriptionCard.displayName = 'RiskDescriptionCard';
